/**
 * Microsoft Entra (Veersa Tech) OIDC helpers.
 *
 * Enabled when VITE_OIDC_AUTHORITY + VITE_OIDC_CLIENT_ID are baked in at
 * image build time (per-environment deploy). Redirect prefers the current
 * browser origin so custom domains and ACA hostnames both work when
 * registered in Entra.
 */

import {
  type AuthenticationResult,
  type Configuration,
  type IPublicClientApplication,
  PublicClientApplication,
} from '@azure/msal-browser'

const authority = (import.meta.env.VITE_OIDC_AUTHORITY as string | undefined)?.trim() || ''
const clientId = (import.meta.env.VITE_OIDC_CLIENT_ID as string | undefined)?.trim() || ''

export function isOidcEnabled(): boolean {
  return Boolean(authority && clientId)
}

export function getOidcRedirectUri(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/auth/callback`
  }
  const fromEnv = (import.meta.env.VITE_OIDC_REDIRECT_URI as string | undefined)?.trim()
  if (fromEnv) return fromEnv
  return '/auth/callback'
}

let pcaPromise: Promise<IPublicClientApplication> | null = null

async function getMsal(): Promise<IPublicClientApplication> {
  if (!isOidcEnabled()) {
    throw new Error('Microsoft sign-in is not configured for this build.')
  }
  if (!pcaPromise) {
    const redirectUri = getOidcRedirectUri()
    const config: Configuration = {
      auth: {
        clientId,
        authority,
        redirectUri,
        postLogoutRedirectUri: typeof window !== 'undefined' ? window.location.origin : redirectUri,
        navigateToLoginRequestUrl: false,
      },
      cache: {
        cacheLocation: 'sessionStorage',
      },
    }
    pcaPromise = PublicClientApplication.createPublicClientApplication(config)
  }
  return pcaPromise
}

const loginRequest = {
  scopes: ['openid', 'profile', 'email'],
}

/** Start the Entra redirect sign-in (leaves the page). */
export async function startMicrosoftSignIn(): Promise<void> {
  const pca = await getMsal()
  await pca.loginRedirect(loginRequest)
}

/** Complete a redirect return; null when this load is not an auth callback. */
export async function completeMicrosoftRedirect(): Promise<AuthenticationResult | null> {
  const pca = await getMsal()
  return pca.handleRedirectPromise()
}

export function oidcConfigSummary(): { authority: string; clientId: string; redirectUri: string } {
  return { authority, clientId, redirectUri: getOidcRedirectUri() }
}
